import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import moment from 'moment';
import { Helmet } from 'react-helmet';

import { RadioOptionsWithConfirm, IQuestion, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import AdditionalPassports from './additionalPassports';
import { IAdditionalPassport } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormAddPIPage1 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE1));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  // state props from store
  const hasAdditionalPassports = useSelector(
    ({ student }: ApplicationState) => student.visa.hasAdditionalPassports,
    shallowEqual
  );

  const defaultArr = useMemo(() => {
    return [];
  }, []);
  const additionalPassports =
    useSelector(({ student }: ApplicationState) => student.visa.additionalPassports, shallowEqual) || defaultArr;

  // constants
  const currentStep = 1;
  const steps = 2;
  const sectionTitle = 'Additional Personal Information';

  // component state
  const [complete, setComplete] = useState(false);
  const [additionalPassportsState, setAdditionalPassportsState] = useState<IAdditionalPassport[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setAdditionalPassportsState([
      ...additionalPassports,
      {
        docNumber: '',
        placeOfIssue: '',
        issuingAuthority: '',
        issueDate: '',
        expiryDate: ''
      }
    ]);
    setShowModal([...additionalPassports.map(() => false), false]);
  }, [additionalPassports]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasAdditionalPassports,
      additionalPassports
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [complete, hasAdditionalPassports, additionalPassports]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deletePassport = (index: number): void => {
    onChange(VQ.ADDITIONAL_PASSPORTS, [
      ...additionalPassports.slice(0, index),
      ...additionalPassports.slice(index + 1)
    ]);
  };

  const onHasAdditionalClick = (question: IQuestion) => {
    onChange(VQ.HAS_ADDITIONAL_PASSPORTS, question.label === 'Yes');
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        key={VQ.HAS_ADDITIONAL_PASSPORTS}
        id={VQ.HAS_ADDITIONAL_PASSPORTS}
        label={'Have you had any other passports in the last 10 years?'}
        subText={'(other than the ones in the document upload section)'}
        onClick={(question: any) => onHasAdditionalClick(question)}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasAdditionalPassports === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasAdditionalPassports === false
          }
        ]}
      />

      {hasAdditionalPassports === true && (
        <CardContainer title={`Additional Passports`}>
          {additionalPassportsState.map((passport: IAdditionalPassport, index: number) => (
            <CardWithModal
              key={index}
              id={`additional-passport-${index}`}
              index={index}
              modalTitle={'Add an Additional Passport'}
              cardItems={[
                {
                  label: 'Document number',
                  value: passport.docNumber
                },
                {
                  label: 'Place of issue',
                  value: passport.placeOfIssue
                },
                {
                  label: 'Issuing authority',
                  value: passport.issuingAuthority
                },
                {
                  label: 'Date of issue',
                  value: moment(passport.issueDate).format('DD MMM YYYY')
                },
                {
                  label: 'Date of expiry',
                  value: moment(passport.expiryDate).format('DD MMM YYYY')
                }
              ]}
              onRemove={deletePassport}
              addButtonLabel={`Add Additional Passport`}
              addAnotherButtonLabel={`I have another passport to add`}
              hasAddMoreButton={
                passport.docNumber === '' &&
                passport.placeOfIssue === '' &&
                passport.issuingAuthority === '' &&
                passport.issueDate === '' &&
                passport.expiryDate === ''
              }
              showModal={showModal[index]}
              toggleModal={toggleModal}
            >
              <AdditionalPassports
                index={index}
                docNumber={passport.docNumber}
                placeOfIssue={passport.placeOfIssue}
                issuingAuthority={passport.issuingAuthority}
                issueDate={passport.issueDate}
                expiryDate={passport.expiryDate}
                onSaveAndClose={(passport: IAdditionalPassport) => {
                  const passports = [...additionalPassports];
                  passports[index] = {
                    ...passport
                  };
                  onChange(VQ.ADDITIONAL_PASSPORTS, passports);
                  toggleModal(index);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}
    </QuestionPage>
  );
};
export default VisaPrepFormAddPIPage1;
